import React from "react";
import MarketingLayout, { SpaLink } from "./MarketingLayout";

const PROVIDER = {
  "@type": "Organization",
  name: "GearCheckAuto",
  url: "https://gearcheckauto.com/",
};

const JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Pre-purchase car inspection",
    name: "Pre-purchase vehicle inspection",
    provider: PROVIDER,
    areaServed: { "@type": "Country", name: "United States" },
    url: "https://gearcheckauto.com/pre-purchase-inspection",
    description:
      "Connect with nearby auto repair shops and mobile mechanics for a pre-purchase inspection before you buy a used car. Send one request, compare quotes and response times, and book.",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://gearcheckauto.com/" },
      {
        "@type": "ListItem",
        position: 2,
        name: "Pre-purchase inspection",
        item: "https://gearcheckauto.com/pre-purchase-inspection",
      },
    ],
  },
];

const CHECKLIST = [
  "Engine and transmission condition, leaks, and fluid quality",
  "Brakes, suspension, steering, and tires",
  "Frame and body for prior accident or rust damage",
  "Battery, charging system, and warning lights",
  "Diagnostic trouble codes pulled with a scan tool",
  "A road test for noises, alignment, and how it drives",
];

export default function PrePurchaseInspectionPage({ onNavigate }) {
  return (
    <MarketingLayout onNavigate={onNavigate} jsonLd={JSON_LD}>
      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 dark:from-blue-950/40 to-white px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">Pre-purchase inspections</p>
          <h1 className="mt-3 text-4xl font-bold leading-tight tracking-tight text-slate-900 dark:text-gray-100 md:text-5xl">
            Get a pre-purchase inspection before you buy a used car
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-gray-300">
            A pre-purchase inspection (PPI) is the smartest money you can spend before buying a used
            vehicle. A trusted local mechanic checks the car from end to end so you know what you are
            really buying - and what it may cost to fix. GearCheckAuto helps you find one fast: send a
            single request to nearby auto repair shops and mobile mechanics, compare quotes and
            response times, and book the best fit.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => onNavigate("signup")}
              className="rounded-lg bg-red-600 px-7 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-500"
            >
              Find an inspection
            </button>
            <button
              type="button"
              onClick={() => onNavigate("autoRepair")}
              className="rounded-lg border border-blue-600 px-7 py-3 text-base font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40"
            >
              Need a repair instead?
            </button>
          </div>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-4 py-12 text-slate-700 dark:text-gray-200">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">What is a pre-purchase inspection?</h2>
        <p className="mt-4 leading-relaxed">
          A pre-purchase inspection is a thorough check of a used car by a qualified mechanic before
          you buy it. Unlike a quick test drive, a PPI looks under the hood and under the car for
          hidden problems - worn brakes, fluid leaks, frame or accident damage, and electronic fault
          codes - so you can buy with confidence or walk away from a bad deal.
        </p>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">Why get one before you buy</h2>
        <p className="mt-4 leading-relaxed">
          A used car can look great and still hide expensive issues. A pre-purchase inspection that
          costs a little up front can save you thousands later, give you real leverage to negotiate
          the price, and tell you whether the seller's description matches the car's true condition.
          It is especially worth it for private-party sales, older vehicles, and any car being sold
          "as is."
        </p>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">What a mechanic checks</h2>
        <p className="mt-4 leading-relaxed">A typical pre-purchase inspection covers:</p>
        <ul className="mt-4 list-disc space-y-2 pl-6">
          {CHECKLIST.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
          How to get a pre-purchase inspection with GearCheckAuto
        </h2>
        <ol className="mt-4 list-decimal space-y-3 pl-6">
          <li>Tell us the vehicle you are considering and where you are - it takes a couple of minutes.</li>
          <li>Send one request to several nearby shops and mobile mechanics at once.</li>
          <li>Compare quotes and response times, then book the inspection that fits you best.</li>
        </ol>
        <p className="mt-4 leading-relaxed">
          Prefer not to drive the car around? Many mobile mechanics will come to the seller's location
          to perform the inspection on site.
        </p>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">What does a pre-purchase inspection cost?</h2>
        <p className="mt-4 leading-relaxed">
          Pricing varies by shop, location, and how detailed the inspection is. Because GearCheckAuto
          lets you send one request to multiple shops, you can compare quotes side by side and choose
          the option that matches your budget instead of calling around one shop at a time.
        </p>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">Common questions</h2>
        <div className="mt-4 space-y-4">
          <div>
            <p className="font-semibold text-slate-900 dark:text-gray-100">How long does a pre-purchase inspection take?</p>
            <p className="mt-1 leading-relaxed">Most take about an hour, though it depends on the vehicle and how thorough the shop is.</p>
          </div>
          <div>
            <p className="font-semibold text-slate-900 dark:text-gray-100">Can a mobile mechanic do a pre-purchase inspection?</p>
            <p className="mt-1 leading-relaxed">
              Yes. Many mobile mechanics on GearCheckAuto will travel to the seller to inspect the car
              where it is parked.
            </p>
          </div>
        </div>
        <p className="mt-6 leading-relaxed">
          More answers are on our <SpaLink view="faq" onNavigate={onNavigate} className="font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-600">FAQ page</SpaLink>.
        </p>
      </article>

      {/* CTA */}
      <section className="bg-slate-900 px-4 py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-white">Inspect before you buy</h2>
          <p className="mt-3 text-slate-300">
            Send one request and let nearby shops and mobile mechanics compete for your inspection.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => onNavigate("signup")}
              className="rounded-lg bg-red-600 px-7 py-3 text-base font-semibold text-white hover:bg-red-500"
            >
              Get started
            </button>
            <button
              type="button"
              onClick={() => onNavigate("contact")}
              className="rounded-lg border border-slate-500 px-7 py-3 text-base font-semibold text-white hover:bg-slate-800"
            >
              Contact us
            </button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
